import React, { useEffect } from 'react';
import './Search.css';
import { getConfig } from '../../config/config-helper';
import { type SearchDriverOptions } from '@elastic/search-ui';
import AppSearchAPIConnector from '@elastic/search-ui-app-search-connector';
import {
  ErrorBoundary,
  Facet,
  Paging,
  PagingInfo,
  Results,
  SearchBox,
  SearchProvider,
  WithSearch,
} from '@elastic/react-search-ui';
import SearchBar from '../controls/SearchBar';
import FacetMultiSelectView from '../controls/FacetMultiSelectView';
import Button from '../controls/Button';
import ProjectCardSkeleton from '../project/ProjectCardSkeleton';
import { selectUser } from '../../reducers/accountSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchDeployments } from '../../reducers/deploymentsSlice';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import Result from '../project/Result';
import { useToken } from '../../hooks/useToken';
import SolutionQuickstartResult from '../project/SolutionQuickstartResult';
import { classNames } from '../../utilities/styleUtils';
import Tag from '../common/Tag';

interface IFilter {
  field: string;
  values: string[];
  type: string;
}

export default function Search({ quickstartResults = false }: { quickstartResults?: boolean }) {
  const currentUser = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const token = useToken();
  const { searchKey, endpointBase, engineName, facets, disjunctiveFacets } = getConfig();
  const connector = new AppSearchAPIConnector({
    searchKey,
    engineName,
    endpointBase,
  });
  const config: SearchDriverOptions = {
    initialState: {
      filters: [
        {
          field: 'listing_kind',
          values: ['solution', 'hyperlink'],
          type: 'any',
        },
      ],
    },
    searchQuery: {
      facets,
      disjunctiveFacets,
    },
    apiConnector: connector,
    alwaysSearchOnInitialLoad: true,
  };

  useEffect(() => {
    if (token === '' || currentUser?.username === '') {
      return;
    }
    if (currentUser?.username !== undefined) {
      dispatch(fetchDeployments({ token, username: currentUser?.username })).catch((err) => {
        console.log('Fetch deployments error:', err);
      });
    }
  }, [token, currentUser?.username]);

  return (
    <SearchProvider config={config}>
      <WithSearch
        mapContextToProps={({ wasSearched, clearFilters, setFilter, removeFilter, filters }) => ({
          wasSearched,
          clearFilters,
          setFilter,
          removeFilter,
          filters,
        })}
      >
        {({ wasSearched, clearFilters, setFilter, removeFilter, filters }) => {
          const currentListingKindVals = filters.find((filter: IFilter) => filter.field === 'listing_kind');
          const currentListingKinds = currentListingKindVals?.values?.length
            ? currentListingKindVals.values
            : ['solution'];

          const resetFilters = () => {
            clearFilters(['listing_kind']);
          };

          return (
            <PageCenteredLayout>
              <ErrorBoundary>
                <div className="space-y-4">
                  {/* Tabs for switching between Solutions and Hands on Labs */}
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      <button
                        onClick={() => {
                          clearFilters();
                          setFilter('listing_kind', ['solution', 'hyperlink'], 'any');
                        }}
                        className={classNames(
                          currentListingKinds.includes('solution')
                            ? 'border-dataops-secondary-dark-blue text-dataops-secondary-dark-blue'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium',
                        )}
                      >
                        Solutions
                      </button>
                      <button
                        onClick={() => {
                          clearFilters();
                          setFilter('listing_kind', ['event_solution'], 'all');
                        }}
                        className={classNames(
                          currentListingKinds.includes('event_solution')
                            ? 'border-dataops-secondary-dark-blue text-dataops-secondary-dark-blue'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                          'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium',
                        )}
                      >
                        Hands on Labs
                      </button>
                    </nav>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="grow">
                      <SearchBox
                        debounceLength={0}
                        inputView={SearchBar}
                        searchAsYouType={true}
                        shouldClearFilters={false}
                        inputProps={{
                          placeholder: `Type to search ${
                            currentListingKinds.includes('solution') ? 'Solutions' : 'Hands on Labs'
                          }`,
                        }}
                      />
                      <div className="hidden"></div>
                    </div>
                    <Popover>
                      <PopoverButton className="flex p-1.5 text-sm text-gray-500 justify-center items-center hover:underline">
                        <div className="">Filters</div>
                        <span className="sr-only">Filters</span>
                        <ChevronDownIcon className="ml-2 h-5 w-5 flex-none" aria-hidden="true" />
                      </PopoverButton>
                      <PopoverPanel
                        anchor="bottom end"
                        style={{ overflow: 'visible' }}
                        transition
                        className="z-50 h-3/4 flex flex-col p-2 transform rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all data-[closed]:scale-50 data-[closed]:opacity-0 data-[closed]:translate-x-20 data-[closed]:-translate-y-20 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[open]:overflow-visible"
                      >
                        <div className="overflow-visible">
                          <div className="w-60">
                            <Facet
                              field="vertical_applicability"
                              label="Vertical"
                              show={30}
                              view={FacetMultiSelectView}
                            />
                          </div>

                          <div className="w-60">
                            <Facet field="use_case" label="Use case" show={30} view={FacetMultiSelectView} />
                          </div>
                          <div className="w-60">
                            <Facet field="workload" label="Workload" show={30} view={FacetMultiSelectView} />
                          </div>
                          <div className="w-60">
                            <Facet field="snowflake_feature" label="Feature" show={30} view={FacetMultiSelectView} />
                          </div>
                          <div className="w-60">
                            <Facet
                              field="release_status"
                              label="Release Status"
                              show={30}
                              view={FacetMultiSelectView}
                            />
                          </div>
                        </div>
                      </PopoverPanel>
                    </Popover>
                    <Button onClick={resetFilters} intent="light" size="medium">
                      Clear Filters
                    </Button>
                  </div>
                  {/* Display applied filters */}
                  <div className="space-x-1">
                    {filters
                      ?.filter((filter: IFilter) => filter.field !== 'listing_kind')
                      .map((filter: IFilter, filterIndex: number) => (
                        <React.Fragment key={`filter-${String(filterIndex)}`}>
                          {filter.values.map((value: string, valueIndex: number) => (
                            <Tag
                              key={`filter-value-${String(filterIndex)}-${String(valueIndex)}`}
                              color="lightBlue"
                              removeCallback={() => {
                                removeFilter(filter.field, value);
                              }}
                            >
                              {filter.field.replace(/_/g, ' ')}: {value}
                            </Tag>
                          ))}
                        </React.Fragment>
                      ))}
                  </div>
                  <div className="flex justify-between text-sm">{(wasSearched as boolean) && <PagingInfo />}</div>
                  <div className="">
                    {token !== '' && currentUser?.username !== '' ? (
                      <Results
                        className="grid grid-cols-2 gap-4"
                        titleField="title"
                        urlField="nps_link"
                        thumbnailField="image_url"
                        shouldTrackClickThrough
                        resultView={(props) => {
                          if (quickstartResults) {
                            return SolutionQuickstartResult({
                              ...props,
                            });
                          }
                          return Result({
                            ...props,
                          });
                        }}
                      />
                    ) : (
                      <ul className="grid grid-cols-2 gap-4">
                        <ProjectCardSkeleton />
                        <ProjectCardSkeleton />
                        <ProjectCardSkeleton />
                        <ProjectCardSkeleton />
                        <ProjectCardSkeleton />
                        <ProjectCardSkeleton />
                        <ProjectCardSkeleton />
                      </ul>
                    )}
                  </div>
                  <div className="pb-16">{(wasSearched as boolean) && <Paging />}</div>
                </div>
              </ErrorBoundary>
            </PageCenteredLayout>
          );
        }}
      </WithSearch>
    </SearchProvider>
  );
}
